/**
 * 查看批量任务详情弹窗
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Divider, Radio, message, Pagination } from 'antd';
import { useBatchTaskFuncTypes } from '@/utils/useBatchTaskEnums';
import { getBatchOptTaskDetail } from '@/service/batchOpt';
import AebParamsReadForm from '../components/AebParamsReadForm';
import HardwareInfoReadForm from '../components/HardwareInfoReadForm';
import CalibrationParamsReadForm from '../components/CalibrationParamsReadForm';
import AebDeviceConfigReadForm from '../components/AebDeviceConfigReadForm';
import AebFunctionSwitchReadForm from '../components/AebFunctionSwitchReadForm';
import TaskExecutionResultList from '../components/TaskExecutionResultList';

const { Group: RadioGroup } = Radio;

interface ViewBatchTaskModalProps {
  visible: boolean;
  data?: any; // 任务基础数据，包含id
  onClose: () => void;
}

const ViewBatchTaskModal: React.FC<ViewBatchTaskModalProps> = (props) => {
  const { visible, data, onClose } = props;
  const [selectedFuncType, setSelectedFuncType] = useState<string | undefined>();
  const [deviceSns, setDeviceSns] = useState<string[]>([]); // 存储拆分后的SN号数组
  const [taskDetail, setTaskDetail] = useState<any[]>([]); // 存储任务详情列表
  const [loading, setLoading] = useState(false);
  const [selectedSn, setSelectedSn] = useState<string | undefined>(); // 当前选中的SN
  const [currentPage, setCurrentPage] = useState(1); // 当前页码
  const [pageSize, setPageSize] = useState(10); // 每页显示数量
  const [totalCount, setTotalCount] = useState(0); // 总数量

  // 获取功能类型枚举
  const { funcTypes } = useBatchTaskFuncTypes();

  // 根据选中的SN获取对应的详情数据
  const currentTaskItem = useMemo(() => {
    return taskDetail.find(item => `${item.sn}` === `${selectedSn}`);
  }, [taskDetail, selectedSn]);

  useEffect(() => {
    if (visible && data?.id) {
      setCurrentPage(1); // 重置页码
      fetchTaskDetail(1, pageSize);
    }
  }, [visible, data]);

  useEffect(() => {
    if (visible && data?.id) {
      fetchTaskDetail(currentPage, pageSize);
    }
  }, [currentPage, pageSize]);

  // 获取任务详情
  const fetchTaskDetail = async (page: number = currentPage, limit: number = pageSize) => {
    if (!data?.id) return;

    setLoading(true);
    try {
      const res = await getBatchOptTaskDetail(data.id, { page, limit });
      if (res.data.success && res.data.data) {
        setTaskDetail(res.data.data);

        // 提取所有SN号
        const snList = res.data.data.map((item: any) => item.sn).filter(Boolean);
        setDeviceSns(snList);

        // 设置总数量
        setTotalCount(res.data.count || 0);

        // 默认选中第一个SN
        const defaultSn = snList.length > 0 ? snList[0] : undefined;
        setSelectedSn(defaultSn);

        // 设置功能类型（只在第一次时设置）
        if (!selectedFuncType) {
          setSelectedFuncType(data.funcType);
        }
      }
    } catch (error) {
      message.error('获取任务详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理SN选择变化
  const handleSnChange = (e: any) => {
    setSelectedSn(e.target.value);
  };

  // 处理页码变化
  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // 改变每页数量时，重置到第一页
    }
  };


  return (
    <Modal
      title="查看详情"
      open={visible}
      onOk={onClose}
      onCancel={onClose}
      width="80%"
      destroyOnClose
      okText="关闭"
      cancelButtonProps={{ style: { display: 'none' } }}
      confirmLoading={loading}
    >
      {/* 第一部分：任务类型选择 */}
      <p>任务类型: {funcTypes.find(item => item.value === selectedFuncType)?.label}</p>
      <Divider />
      {/* 第二部分：设备SN号单选按钮组 */}
      <Form.Item label="设备SN号">
        <RadioGroup value={selectedSn} onChange={handleSnChange}>
          {deviceSns.map((sn, index) => (
            <Radio key={index} value={sn}>
              {sn}
            </Radio>
          ))}
        </RadioGroup>
        <div style={{ marginTop: '16px' }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalCount}
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
            showSizeChanger
            showQuickJumper
            showTotal={(total) => `共 ${total} 条`}
            pageSizeOptions={['10', '20', '50']}
          />
        </div>
      </Form.Item>

      <Divider />
      {/* 第三部分：任务相关表单 */}
      {selectedFuncType && currentTaskItem && (
        <>
          {/* 读取功能：显示对应的读取表单 */}
          {selectedFuncType === 'AEB_CONFIG_GET' && (
            <AebParamsReadForm data={currentTaskItem} />
          )}
          {selectedFuncType === 'AEB_VERSION_GET' && (
            <HardwareInfoReadForm data={currentTaskItem} />
          )}
          {selectedFuncType === 'AEB_CALIBRATION_GET' && (
            <CalibrationParamsReadForm data={currentTaskItem} />
          )}
          {selectedFuncType === 'AEB_SWITCH_CONFIG_GET' && (
            <AebDeviceConfigReadForm data={currentTaskItem} />
          )}
          {selectedFuncType === 'AEB_FUNCTION_SWITCH_GET' && (
            <AebFunctionSwitchReadForm data={currentTaskItem} />
          )}

          {/* 设置功能：显示执行结果 */}
          {
            (
              selectedFuncType === 'AEB_CALIBRATION_SET' ||
              selectedFuncType === 'AEB_CONFIG_SET' ||
              selectedFuncType === 'AEB_FUNCTION_SWITCH_SET' ||
              selectedFuncType === 'AEB_SWITCH_CONFIG_SET'
            )
            && (
              <TaskExecutionResultList data={taskDetail} />
            )
          }
        </>
      )}
    </Modal>
  );
};

export default ViewBatchTaskModal;